import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { actionCredits } from 'src/app/services/operators/voyageradar-credit-actions';
import {
  buildVoyageTrackGroups,
  voyageTrackSummary,
  VoyageTrackGroup,
  VoyageTrackRow,
  VoyageTrackSummary,
  VOYAGE_TRACKS_RECENT,
} from './voyage-track-history.model';

/**
 * Voyage Radar "Vessel Tracks" — the purchased per-vessel paths for a sensor.
 *
 * The fourth panel section, and the only one about tracks rather than AOI
 * snapshots. Its siblings each answer one question about the snapshot archive:
 *   ON MAP bar      — what is drawn right now (a snapshot OR a track).
 *   Vessel Types    — which of the drawn vessels are visible.
 *   Refresh History — what else could be drawn.
 *   this            — which vessels this task has bought a path for.
 *
 * Grouped by vessel rather than by day, because that is the question a paid
 * asset raises: "what do I already own for this hull". Expanding a group lists
 * every window bought for it, newest fetch first.
 *
 * Before this section existed, a purchased track was reachable only through the
 * map toolbar's trace drawer, which filters to the MMSI of the vessel currently
 * clicked. A vessel that had drifted out of the live snapshot therefore had no
 * route back to a track the task had already paid for. Recovering those is the
 * point of the presence dot and of playing straight from a row.
 *
 * Purely presentational: it knows nothing about Leaflet, S3 or the API. The host
 * decides what playing means and feeds the result back through `activeTrackId`,
 * so the section and the ON MAP bar can never disagree about state.
 *
 * One instance per sensor — it sits inside the host's `*ngFor` over
 * `collection.sensors`.
 *
 * Change detection stays Default to match its siblings: the host mutates
 * `sensor.trackHistory` in place when a paid fetch lands.
 */
@Component({
  selector: 'app-voyage-track-history',
  templateUrl: './voyage-track-history.component.html',
  styleUrls: ['./voyage-track-history.component.scss'],
})
export class VoyageTrackHistoryComponent implements OnChanges {
  /** The Voyage Radar sensor whose `trackHistory` this renders. */
  @Input() sensor: any;
  /** trackId of the track currently on the map, or null. */
  @Input() activeTrackId: string | null = null;
  /** True while any track load is in flight — gates the row actions. */
  @Input() trackLoading = false;
  /**
   * trackId of the load worth DRAWING, or null.
   *
   * Separate from `trackLoading` because the two answer different questions.
   * That one gates the click and is true from the first millisecond, so a
   * double-click cannot start two loads; this one arrives ~150ms later and says
   * which row should show it is working. Binding the spinner to the gate would
   * blink it on every fast replay, which reads as a glitch rather than as speed.
   *
   * A row, not a boolean, because the panel can hold a dozen hulls: the answer
   * belongs on the row that was clicked, where the pointer already is, and
   * nowhere else.
   */
  @Input() loadingTrackId: string | null = null;
  /**
   * MMSI → live name/colour for the vessels in the snapshot on the map. Drives
   * the presence dot, the group colour, and the preference for a name being
   * broadcast now over one stored with a track last week.
   */
  @Input() liveVessels: Map<string, { name?: string | null; color?: string | null }> | null = null;

  /** Play a track on the map. Emits the raw trackHistory record. */
  @Output() play = new EventEmitter<any>();
  /** Take the playing track off the map — emitted by re-clicking its row. */
  @Output() stop = new EventEmitter<void>();
  /** A row's download button. Emits the record. */
  @Output() download = new EventEmitter<any>();

  /** Expanded by default: this section IS the feature, and it is short when
   *  every group is collapsed. Vessel Types opens on request for the opposite
   *  reason — its content is a chip list that fills the sidebar. */
  expanded = true;

  /**
   * Which vessel's tracks are open, as an accordion rather than a set. A sensor
   * can hold tracks for a dozen hulls and expanding several at once turns a
   * 320px section into a scroll — which is exactly what capping the snapshot
   * list was meant to prevent.
   */
  expandedMmsi: string | null = null;

  /**
   * Reveals the vessel groups past `VOYAGE_TRACKS_RECENT`.
   *
   * Its snapshot sibling caps hard and sends the rest to the archive modal.
   * There is no track archive yet, so a hard cap here would strand purchased
   * tracks with no way to reach them — the exact failure this section exists to
   * fix. When the modal ships this toggle becomes its handoff button.
   */
  showAll = false;

  /**
   * True while the pointer is over a child of a row that carries its own
   * tooltip — the action buttons, the PARTIAL/CLAMPED flags.
   *
   * A row's tooltip and its children's are nested, and `mouseleave` does not
   * fire on the row when the pointer moves onto a descendant, so both stayed
   * open and Material drew them on top of each other under the row.
   * The row's binding reads this to stand down while a child speaks.
   *
   * One field, not one per row: only one element is under the pointer at a
   * time, and `matTooltipDisabled` hides an open tooltip the moment it is set.
   */
  childTipHover = false;

  private _cacheKey = '';
  private _groups: VoyageTrackGroup[] = [];
  private _summaryKey = '';
  private _summary: VoyageTrackSummary = { tracks: 0, vessels: 0 };

  get history(): any[] {
    return Array.isArray(this.sensor?.trackHistory) ? this.sensor.trackHistory : [];
  }

  /**
   * What a track costs, from this sensor's catalogue snapshot.
   *
   * The empty-state copy quotes it. It was a literal 5 in the template, which
   * would have gone on saying "5 credits" after any ee-admin re-price — the one
   * number on this panel a user might act on, and the only one nothing kept
   * honest.
   */
  get trackCost(): number {
    return actionCredits(this.sensor, 'track');
  }

  /**
   * Usable records, not raw array length — malformed rows never render, so
   * counting the raw array could advertise four tracks above a list showing two.
   *
   * Memoised: the template reads this five times per binding pass and each read
   * re-normalises the whole history, which the track controller's ~10-per-second
   * emissions would otherwise multiply out while a track plays.
   */
  get summary(): VoyageTrackSummary {
    const history = this.history;
    const key = [history.length, history[history.length - 1]?.trackId].join('|');
    if (key !== this._summaryKey) {
      this._summaryKey = key;
      this._summary = voyageTrackSummary(history);
    }
    return this._summary;
  }

  get hasMore(): boolean {
    return this.summary.vessels > VOYAGE_TRACKS_RECENT;
  }

  get moreLabel(): string {
    const { tracks, vessels } = this.summary;
    return this.showAll
      ? 'Show fewer'
      : `Show all ${tracks} ${tracks === 1 ? 'track' : 'tracks'} · ${vessels} vessels`;
  }

  /**
   * Vessel groups, memoised. Angular re-evaluates template bindings on every
   * change-detection pass, and rebuilding groups (which sorts, buckets and
   * formats every record) on each one would be wasted work at 10 frames a
   * second while a track plays.
   *
   * The key carries the newest trackId as well as the length because a paid
   * fetch pushes onto the same array instance, and the live-vessel signature
   * because a refresh can rename or recolour a hull without touching history.
   */
  get groups(): VoyageTrackGroup[] {
    const history = this.history;
    const key = [
      history.length,
      history[history.length - 1]?.trackId,
      this.showAll,
      this.liveVessels?.size ?? 0,
    ].join('|');
    if (key === this._cacheKey) return this._groups;

    this._cacheKey = key;
    this._groups = buildVoyageTrackGroups(history, {
      now: Date.now(),
      limit: this.showAll ? null : VOYAGE_TRACKS_RECENT,
      liveVessels: this.liveVessels,
    });
    return this._groups;
  }

  ngOnChanges(changes: SimpleChanges): void {
    // A track can start from somewhere other than this list — the map toolbar's
    // trace drawer, or a paid fetch that plays on arrival. Open its group so the
    // section shows the track that is on the map rather than an unrelated three.
    if (changes['activeTrackId'] && this.activeTrackId) {
      const owner = this.history.find((t: any) => t?.trackId === this.activeTrackId);
      const mmsi = owner ? String(owner.mmsi) : null;
      if (mmsi && mmsi !== this.expandedMmsi) {
        // Past the cap the group is not rendered at all, so lift the cap too.
        if (!this.groups.some((g) => g.mmsi === mmsi)) this.showAll = true;
        this.expandedMmsi = mmsi;
      }
    }
  }

  toggle(): void {
    this.expanded = !this.expanded;
  }

  toggleShowAll(event: Event): void {
    event.stopPropagation();
    this.showAll = !this.showAll;
  }

  toggleGroup(group: VoyageTrackGroup): void {
    this.expandedMmsi = this.expandedMmsi === group.mmsi ? null : group.mmsi;
  }

  isOpen(group: VoyageTrackGroup): boolean {
    return this.expandedMmsi === group.mmsi;
  }

  isOnMap(row: VoyageTrackRow): boolean {
    return !!this.activeTrackId && row.trackId === this.activeTrackId;
  }

  /**
   * True while THIS row's track is being recalled.
   *
   * Yields to `isOnMap`: when a load lands, the track becomes the active one on
   * the same emission that ends the load, and a row showing both states at once
   * would read as "loading something that is already here".
   */
  isLoading(row: VoyageTrackRow): boolean {
    return this.loadingTrackId === row.trackId && !this.isOnMap(row);
  }

  /** True when a group holds the track currently drawn — badges it when closed. */
  groupOnMap(group: VoyageTrackGroup): boolean {
    return group.rows.some((r) => this.isOnMap(r));
  }

  /**
   * True when a group holds the track being recalled — badges it when closed.
   *
   * A replay can be started from outside this list (the map toolbar's trace
   * drawer), and the group it belongs to is not opened until the track lands.
   * Without this the panel would sit still through a wait it is about to
   * explain.
   */
  groupLoading(group: VoyageTrackGroup): boolean {
    return group.rows.some((r) => this.isLoading(r));
  }

  /**
   * Row click. Re-clicking the playing track takes it off the map, mirroring the
   * snapshot list's toggle so the two behave the same way.
   */
  pick(row: VoyageTrackRow): void {
    // A single-position track has no path to draw and the transport would
    // refuse it, so the row is inert rather than a click that ends in an error
    // dialog. The NO PATH flag and the row tooltip carry the reason, and the
    // download button beside it still works.
    if (this.trackLoading || row.pathless) return;
    if (this.isOnMap(row)) this.stop.emit();
    else this.play.emit(row.data);
  }

  downloadRow(row: VoyageTrackRow, event: Event): void {
    event.stopPropagation();
    this.download.emit(row.data);
  }

  /** Keeps *ngFor from tearing down every row when a fetch pushes a new one. */
  trackByMmsi(_i: number, group: VoyageTrackGroup): string {
    return group.mmsi;
  }

  trackByTrackId(_i: number, row: VoyageTrackRow): string {
    return row.trackId;
  }
}
